import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from '~/components/ConfirmDialog'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { ACCOMMODATION_STATUS_KEYS, ACCOMMODATION_STATUS_VARIANTS } from '~/helpers/accommodations/accommodation-labels'
import { useBranding } from '~/helpers/branding/branding-context'
import { tourAnchor } from '~/helpers/tour/anchors'
import AccommodationForm from './AccommodationForm'
import { useAccommodation } from './use-accommodation'

/**
 * « Besoin d'un aménagement ? » sur « Mon compte » (#327) : à qui s'adresser, et
 * la demande de l'apprenant — l'envoyer, la modifier, la retirer, lire la réponse.
 */
function AccommodationCard() {
  const { t } = useTranslation()
  const { appName } = useBranding()
  const [editing, setEditing] = useState(false)
  const [withdrawing, setWithdrawing] = useState(false)
  const { accommodation, busy, submit, withdraw } = useAccommodation(() => {
    setEditing(false)
    setWithdrawing(false)
  })
  if (!accommodation) return null
  const { referent, request } = accommodation
  const referentLabel = [referent.name, referent.contact].filter(Boolean).join(' — ')

  return (
    <Card {...tourAnchor('account.accommodation')}>
      <CardHeader>
        <CardTitle>{t('accommodation.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">{t('accommodation.intro')}</p>
        <p className="text-sm">
          {referentLabel ? t('accommodation.referent', { who: referentLabel }) : t('accommodation.noReferent')}
        </p>
        {request && !editing
          ? (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{t('accommodation.yourRequest')}</span>
                  <Badge variant={ACCOMMODATION_STATUS_VARIANTS[request.status]}>{t(ACCOMMODATION_STATUS_KEYS[request.status])}</Badge>
                </div>
                <p className="whitespace-pre-line rounded-md bg-muted p-3 text-sm">{request.needs}</p>
                {request.response && (
                  <p className="text-sm">
                    <span className="font-medium">{t('accommodation.response')}</span>
                    {' '}
                    {request.response}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => setEditing(true)}>{t('accommodation.edit')}</Button>
                  <Button variant="ghost" onClick={() => setWithdrawing(true)}>{t('accommodation.withdraw')}</Button>
                </div>
              </div>
            )
          : (
              <AccommodationForm
                initialNeeds={request?.needs}
                orgName={appName}
                busy={busy}
                onSubmit={needs => void submit(needs)}
                onCancel={request ? () => setEditing(false) : undefined}
              />
            )}
      </CardContent>
      <ConfirmDialog open={withdrawing} title={t('accommodation.withdrawConfirm.title')} confirmLabel={t('accommodation.withdraw')} pending={busy} onConfirm={() => void withdraw()} onCancel={() => setWithdrawing(false)}>
        {t('accommodation.withdrawConfirm.body')}
      </ConfirmDialog>
    </Card>
  )
}

export default AccommodationCard
